import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export default function MarkdownEditor({ value, onSave, placeholder = "Add a more detailed description... (markdown supported)" }) {
  const [draft, setDraft] = useState(value || "");
  const [tab, setTab] = useState("write"); // "write" | "preview"
  const [editing, setEditing] = useState(false);

  // Keep draft synced when parent value changes externally
  if (!editing && draft !== (value || "")) {
    setDraft(value || "");
  }

  const save = () => {
    if (draft !== (value || "")) onSave(draft);
    setEditing(false);
    setTab("write");
  };

  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
    setTab("write");
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="w-full text-left border border-zinc-200 hover:border-black active:border-black transition-colors p-3 min-h-[100px] text-sm"
        data-testid="markdown-description-display"
      >
        {value ? (
          <div className="prose-kanbn">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
          </div>
        ) : (
          <span className="text-zinc-400 italic">{placeholder}</span>
        )}
      </button>
    );
  }

  return (
    <div className="border border-black" data-testid="markdown-description-editor">
      <div className="flex border-b border-zinc-200">
        <button
          type="button"
          onClick={() => setTab("write")}
          className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] transition-colors ${
            tab === "write" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
          }`}
          data-testid="markdown-tab-write"
        >
          Write
        </button>
        <button
          type="button"
          onClick={() => setTab("preview")}
          className={`flex-1 sm:flex-none px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] transition-colors ${
            tab === "preview" ? "bg-zinc-900 text-white" : "text-zinc-600 hover:bg-zinc-50"
          }`}
          data-testid="markdown-tab-preview"
        >
          Preview
        </button>
        <div className="flex-1 hidden sm:block" />
      </div>

      {tab === "write" ? (
        <textarea
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={6}
          placeholder={placeholder}
          className="w-full px-3 py-2 text-base sm:text-sm focus:outline-none resize-y min-h-[120px]"
          data-testid="markdown-textarea"
        />
      ) : (
        <div className="p-3 prose-kanbn min-h-[120px]" data-testid="markdown-preview-pane">
          {draft.trim() ? (
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{draft}</ReactMarkdown>
          ) : (
            <span className="text-zinc-400 italic">Nothing to preview yet.</span>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 p-2 border-t border-zinc-200 bg-zinc-50">
        <button
          type="button"
          onClick={save}
          className="bg-black text-white px-4 py-2 text-xs font-medium hover:bg-zinc-800"
          data-testid="markdown-save"
        >
          Save
        </button>
        <button
          type="button"
          onClick={cancel}
          className="px-3 py-2 text-xs font-medium text-zinc-500 hover:text-black"
          data-testid="markdown-cancel"
        >
          Cancel
        </button>
        <span className="ml-auto font-mono-tight text-[10px] uppercase tracking-widest text-zinc-400 hidden sm:inline">
          Markdown · GFM
        </span>
      </div>
    </div>
  );
}
